import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PingStore } from '../stores/ping.store';
import { catchError, interval, map, Observable, of, startWith, switchMap, take, tap } from 'rxjs';
import { PingResult } from '../../models/app/ping-result';
import { MapPipe } from '../../shared/pipes/map.pipe';

@Injectable({
  providedIn: 'root',
})
export class PingService {
  private readonly mapPipe = inject(MapPipe);
  private readonly http = inject(HttpClient);
  readonly store = inject(PingStore);

  private readonly PING_URL = '/api/v1/ping';

  // Store ping statistics
  private latencies: number[] = [];

  /**
   * Starts the pinging observable
   * @param intervalMs Interval between pings in milliseconds (default: 1000ms)
   * @param iteration Number of pings to perform; if undefined, ping infinitely (default: 5)
   * @returns Observable emitting PingResult
   */
  ping(intervalMs = 1000, iteration: number | undefined = 5): Observable<PingResult> {
    // Build the observable pipeline
    const source$ = interval(intervalMs).pipe(
      startWith(0),
      iteration !== undefined ? take(iteration) : (obs$) => obs$, // Infinite if iteration undefined
      map((_, index) => index),
      switchMap((index) =>
        this.pingOnce().pipe(
          map((result) => {
            if (iteration !== undefined && index === iteration - 1) {
              result.status = 'sucess';
            }
            return result;
          }),
        ),
      ),
      map(this.computePingStatistics.bind(this)),
      map(this.computeNetworkQuality.bind(this)),
      tap((result: PingResult) => {
        if (result.status === 'in_progress' && result.latencyMs >= 0) {
          this.updateStatistics(result.latencyMs);
          const aggregatedResult = this.getAggregatedResult(result.latencyMs);
          this.store.update(aggregatedResult);
        } else {
          this.store.update(result);
        }
      }),
    );

    return source$;
  }

  /**
   * Execute a single HTTP HEAD request to measure latency
   */
  private pingOnce(): Observable<PingResult> {
    const start = performance.now();

    return this.http.head(this.PING_URL, { observe: 'response' }).pipe(
      map(() => {
        const end = performance.now();
        return {
          latencyMs: Math.round(end - start),
          timestamp: new Date(),
          status: 'in_progress',
        } as PingResult;
      }),
      catchError(() => {
        return of({
          latencyMs: -1,
          timestamp: new Date(),
          status: 'error',
        } as PingResult);
      }),
    );
  }

  /**
   * Update the array of latencies for statistics
   * @param latency current ping latency in ms
   */
  private updateStatistics(latency: number): void {
    this.latencies.push(latency);
  }

  /**
   * Compute aggregated metrics from collected latencies
   * @param latestLatency last measured latency
   * @returns aggregated PingResult with statistics
   */
  private getAggregatedResult(latestLatency: number): PingResult {
    const count = this.latencies.length;

    // Compute average
    const avgLatency = this.latencies.reduce((a, b) => a + b, 0) / count;

    // Compute min and max
    const minLatency = Math.min(...this.latencies);
    const maxLatency = Math.max(...this.latencies);

    // Compute jitter: average absolute difference between consecutive pings
    let jitter = 0;
    if (count > 1) {
      const diffs = [];
      for (let i = 1; i < this.latencies.length; i++) {
        diffs.push(Math.abs(this.latencies[i] - this.latencies[i - 1]));
      }
      jitter = diffs.reduce((a, b) => a + b, 0) / diffs.length;
    }

    // Compute quality as an example metric (here based on avg latency)
    const quality = this.computeQuality(avgLatency);

    return {
      latencyMs: latestLatency,
      timestamp: new Date(),
      status: 'in_progress',
      min: minLatency,
      max: maxLatency,
      jitter: parseFloat(jitter.toFixed(2)),
      clientIp: '', // set if you want
      quality: quality,
    } as PingResult;
  }

  /**
   * Compute quality metric
   * @param avgLatency average latency in ms
   */
  private computeQuality(avgLatency: number): number {
    if (avgLatency < 50) return 5.0;
    if (avgLatency < 100) return 4.0;
    if (avgLatency < 150) return 3.0;
    if (avgLatency < 200) return 2.0;
    return 1.0;
  }

  calculateJitter(values: number[]): number {
    if (values.length < 2) {
      return 0;
    }

    let sumDiffs = 0;

    for (let i = 1; i < values.length; i++) {
      sumDiffs += Math.abs(values[i] - values[i - 1]);
    }

    const jitter = sumDiffs / (values.length - 1);

    return jitter;
  }

  /**
   * Computes a network quality score from various metrics.
   *
   * @param avgLatency - Average ping latency in ms
   * @param jitter - Jitter in ms
   * @param downstreamMbps - Download bandwidth in Mbps
   * @param upstreamMbps - Upload bandwidth in Mbps
   * @returns Quality score between 1 and 5
   */
  private computeNetworkQuality(result: PingResult): PingResult {
    // Map latency (lower is better)
    let latencyScore = this.mapPipe.transform(result.latencyMs, 0, 200, 5, 1);
    latencyScore = Math.max(1, Math.min(5, latencyScore));

    // Map jitter (lower is better)
    let jitterScore = this.mapPipe.transform(result.jitter, 0, 30, 5, 1);
    jitterScore = Math.max(1, Math.min(5, jitterScore));

    // Compute weighted average for ping quality
    result.mos = parseFloat((latencyScore * 0.6 + jitterScore * 0.4).toFixed(2));

    return result;
  }

  /**
   * Populates the min, max, and jitter values in a PingResult
   * based on a list of collected latencies.
   *
   * @param result - The PingResult object to update.
   * @param latencies - Array of latency measurements in ms.
   * @returns Updated PingResult with min, max, and jitter values.
   */
  private computePingStatistics(result: PingResult): PingResult {
    const latencies = this.latencies;
    if (latencies.length === 0) {
      result.min = 0;
      result.max = 0;
      result.jitter = 0;
      return result;
    }

    // Compute min and max latency
    result.min = Math.min(...latencies);
    result.max = Math.max(...latencies);

    // Compute jitter as average absolute difference between consecutive latencies
    result.jitter = this.calculateJitter(latencies);

    return result;
  }
}
